
  import { defineComponent, computed, nextTick, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import {
    DeeplinkApiResult,
    DeeplinkDestination,
  } from '@/store/modules/Notifications/DeeplinkNotificationsModule';
  import {
    buildDeeplink,
    DATE_RANGE_DESTINATIONS,
    DEEPLINK_BASE,
    destinationParams,
    isEmpty,
    ParamSpec,
    parseDeeplink,
    useDeeplinkLookups,
  } from '@/core/helpers/deeplink';

  /**
   * Optional deeplink for a banner: once its checkbox is ticked, picks an
   * in-app screen and its params and v-models the resulting deeplink ('' when
   * unticked). Call validate() before saving.
   */
  export default defineComponent({
    name: 'deeplink-builder',
    props: {
      modelValue: { type: String, default: '' },
    },
    emits: ['update:modelValue'],
    setup(props, { emit, expose }) {
      const { t, te } = useI18n();
      const store = useStore();

      const translate = (text) => (te(text) ? t(text) : text);

      // Only the saved link is needed; later changes come from this component.
      // eslint-disable-next-line vue/no-setup-props-destructure
      const initialLink = props.modelValue;
      const enabled = ref(!!initialLink);
      const advanced = ref(false);
      const rawDeeplink = ref('');
      const destination = ref('');
      const lang = ref('ar');
      const params = ref<Record<string, unknown>>({});
      // Params read from the initial link, applied once its type is selected.
      let pendingParams: Record<string, string> | null = null;

      /* Destinations */
      const destinations = ref<Array<DeeplinkDestination>>([]);
      const destinationsLoading = ref(false);
      const destinationsError = ref('');
      const forbidden = ref(false);

      const screenLabel = (item: DeeplinkDestination) => {
        const key = `deeplinkScreen_${item.destination}`;
        return te(key) ? t(key) : item.screen;
      };

      const selectedDestination = computed(() =>
        destinations.value.find((d) => d.destination === destination.value)
      );
      const currentParams = computed(() =>
        destinationParams(selectedDestination.value)
      );

      const paramLabel = (key: string) => {
        const specific = `deeplinkParam_${destination.value}_${key}`;
        if (te(specific)) return t(specific);
        const generic = `deeplinkParam_${key}`;
        return te(generic) ? t(generic) : key;
      };

      const { isLookupLoading, isWaitingOnParent, paramOptions } =
        useDeeplinkLookups(store, translate, currentParams, params);

      const selectPlaceholder = (spec: ParamSpec, key: string) =>
        spec.dependsOn && isWaitingOnParent(spec)
          ? t('deeplinkSelectFirst', { field: paramLabel(spec.dependsOn) })
          : translate(key);

      // Lookup options use numeric ids, while a parsed link has strings.
      const fromLink = (value: string) =>
        /^\d+$/.test(value) ? Number(value) : value;

      watch(destination, () => {
        const initial = pendingParams || {};
        pendingParams = null;
        const next: Record<string, unknown> = {};
        currentParams.value.forEach((p) => {
          const value = initial[p.key] ?? '';
          if (p.spec.input === 'multiselect') {
            next[p.key] = value ? value.split(',').map(fromLink) : [];
          } else if (p.spec.lookup && value) {
            next[p.key] = fromLink(value);
          } else {
            next[p.key] = value;
          }
        });
        params.value = next;
      });

      const applyLink = (link: string) => {
        const parsed = link ? parseDeeplink(link) : null;
        const known =
          parsed &&
          destinations.value.some((d) => d.destination === parsed.destination);
        if (link && !known) {
          // Not something the builder can show, so keep it as typed.
          advanced.value = true;
          rawDeeplink.value = link;
          return;
        }
        advanced.value = false;
        rawDeeplink.value = '';
        if (!parsed) return;
        lang.value = parsed.lang === 'en' ? 'en' : 'ar';
        pendingParams = parsed.params;
        destination.value = parsed.destination;
      };

      const loadDestinations = async () => {
        destinationsLoading.value = true;
        destinationsError.value = '';
        const res: DeeplinkApiResult<Array<DeeplinkDestination>> =
          await store.dispatch(Actions.GET_DEEPLINK_DESTINATIONS);
        destinationsLoading.value = false;
        if (res.status === 403) {
          forbidden.value = true;
        } else if (!res.data) {
          destinationsError.value =
            res.message || translate('deeplinkLoadDestinationsFailed');
        } else {
          destinations.value = res.data;
          applyLink(props.modelValue);
        }
      };

      /* Deeplink */
      // The builder can't show screens it couldn't load, so keep the saved
      // link as is rather than clearing it.
      const unavailable = computed(
        () => forbidden.value || !destinations.value.length
      );

      const deeplink = computed(() => {
        if (!enabled.value) return '';
        if (unavailable.value) return initialLink;
        return advanced.value
          ? rawDeeplink.value.trim()
          : buildDeeplink(
              destination.value,
              currentParams.value,
              params.value,
              lang.value
            );
      });

      watch(deeplink, (value) => {
        if (value !== props.modelValue) emit('update:modelValue', value);
      });

      const copied = ref(false);
      const copyDeeplink = async () => {
        try {
          await navigator.clipboard.writeText(deeplink.value);
        } catch (e) {
          const input = document.createElement('textarea');
          input.value = deeplink.value;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          document.body.removeChild(input);
        }
        copied.value = true;
        setTimeout(() => (copied.value = false), 1500);
      };

      const clear = () => {
        rawDeeplink.value = '';
        destination.value = '';
      };

      /* Validation */
      const errors = computed(() => {
        const found: Record<string, string> = {};
        if (!enabled.value || unavailable.value) return found;
        if (advanced.value) {
          const link = rawDeeplink.value.trim();
          if (!link) {
            found.rawDeeplink = translate('deeplinkFieldRequired');
          } else if (!/^https:\/\/\S+$/.test(link)) {
            found.rawDeeplink = translate('deeplinkLinkInvalid');
          }
          return found;
        }
        if (!destination.value) {
          found.destination = translate('deeplinkFieldRequired');
          return found;
        }
        currentParams.value.forEach(({ key, required, spec }) => {
          const value = params.value[key];
          if (isEmpty(value)) {
            if (required) found[key] = translate('deeplinkFieldRequired');
            return;
          }
          const text = String(value).trim();
          if (spec.input === 'number' && !/^[1-9]\d*$/.test(text)) {
            found[key] = translate('deeplinkWholeNumber');
          } else if (
            (spec.input === 'hash' || spec.input === 'auction') &&
            !/^\S+$/.test(text)
          ) {
            found[key] = translate('deeplinkNoSpaces');
          }
        });
        const { dateFrom, dateTo } = params.value;
        if (
          DATE_RANGE_DESTINATIONS.includes(destination.value) &&
          dateFrom &&
          dateTo &&
          String(dateFrom) > String(dateTo)
        ) {
          found.dateTo = translate('deeplinkDateOrder');
        }
        return found;
      });

      // Errors show only after the parent first tries to save.
      const showErrors = ref(false);
      const shownErrors = computed(() =>
        showErrors.value ? errors.value : {}
      );

      const validate = async () => {
        showErrors.value = true;
        await nextTick();
        return Object.keys(errors.value).length === 0;
      };

      expose({ validate });

      // Only load the screens once the deeplink is turned on.
      watch(
        enabled,
        (value) => {
          if (
            value &&
            !destinations.value.length &&
            !destinationsLoading.value
          ) {
            loadDestinations();
          }
        },
        { immediate: true }
      );

      return {
        translate,
        DEEPLINK_BASE,
        enabled,
        advanced,
        rawDeeplink,
        destination,
        lang,
        params,
        destinations,
        destinationsLoading,
        destinationsError,
        forbidden,
        loadDestinations,
        screenLabel,
        selectedDestination,
        currentParams,
        paramLabel,
        paramOptions,
        isLookupLoading,
        isWaitingOnParent,
        selectPlaceholder,
        deeplink,
        copied,
        copyDeeplink,
        clear,
        shownErrors,
      };
    },
  });
